// External imports
import React from "react";
import { HiOutlineArrowRight } from "react-icons/hi2";

// Internal imports
import WebsiteButton from "../../components/button/WebsiteButton";
import MainPageLayout from "../../components/header/MainPageLayout";
import NoPermission from "../../components/no-permission/NoPermission";
import { MaintenanceBanner } from "../../components/maintenance-banner/MaintenanceBanner";
//@ts-ignore
import outside from "./outside.jpg";
import {
    PageContainer,
    BackgroundCard,
    ButtonsCard,
    Button,
} from "./FrontPage.styles";
import {
    getJwt,
    getUserRoleAndEntity,
} from "../../core/LocalStorage/AuthSession/core";

// Dependency injection
import { GetUserRoleAndEntity } from "../../core/LocalStorage/AuthSession/core";

// Language Context
import { useLanguage } from "../../core/translations/LanguageContext";

// Translations
import { FrontPageTranslations } from "./translations/types";
import { enTranslations } from "./translations/en";
import { deTranslations } from "./translations/de";
import { SupportedLanguage } from "../../core/translations/supportedLanguages";

/**
 * Props for the FrontPage component
 * @property getRoleApiCall - Function to fetch the user's role
 */
export interface FrontPageProps {}

const TRANSLATIONS: Record<SupportedLanguage, FrontPageTranslations> = {
    en: enTranslations,
    de: deTranslations,
};

/**
 * Front page component of the website
 * Displays main content, hero section, and conditional navigation based on user auth state
 */
export const FrontPage: React.FC<FrontPageProps> = () => {
    // Use the language context instead of localStorage directly
    const { language } = useLanguage();
    // @ts-ignore
    const translations = TRANSLATIONS[language];

    // Check if the user is logged in
    const loggedIn = getJwt();

    // Get the user's role if logged in
    const role_and_entity = getUserRoleAndEntity();

    // Determine the buttons to show based on user role
    const renderButtons = () => {
        if (!loggedIn) return null;

        if (role_and_entity?.role === "SUPERADMIN") {
            // A superadmin manages users by drilling in from institutions, so a
            // single entry point is all that is needed here. (The old "/users"
            // button pointed at a route that does not exist.)
            return <Button href={`/institutions`}>View Institutions</Button>;
        }

        if (role_and_entity?.role === "ADMIN") {
            if (role_and_entity?.entityType === "institution") {
                // Land on the institution overview so an institution admin can
                // reach both its departments and all of its users.
                return (
                    <Button href={`/institution/${role_and_entity?.entityId}`}>
                        View Institution
                    </Button>
                );
            }
            if (role_and_entity?.entityType === "department") {
                return (
                    <Button
                        href={`/department/${role_and_entity?.entityId}/users`}
                    >
                        View Users
                    </Button>
                );
            }
        }

        // Signed in, but not as an admin. Login turns these users away now, so
        // this only catches a session that predates that — say it out loud
        // rather than leaving them on an empty page.
        return <NoPermission />;
    };

    return (
        <MainPageLayout>
            <MaintenanceBanner />
            <PageContainer>
                <BackgroundCard>
                    <ButtonsCard>{renderButtons()}</ButtonsCard>
                </BackgroundCard>
            </PageContainer>
        </MainPageLayout>
    );
};
